import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt-br';
import { Bell, BellRing, CheckCheck, Loader2, Settings, Trophy, UserPlus, X } from 'lucide-react';
import { useRealtime, InboxItem } from '../contexts/RealtimeContext';
import InstallAppButton from './InstallAppButton';

moment.locale('pt-br');

// Sino flutuante de notificações: canto superior direito no celular, inferior direito no desktop.
const NotificationBell: React.FC = () => {
  const {
    unreadNotifications,
    lastEvent,
    inbox,
    inboxLoading,
    hasMoreInbox,
    loadInbox,
    markNotificationsRead,
    markAllNotificationsRead,
    pushStatus,
    enablePush,
  } = useRealtime();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isRinging, setIsRinging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Balança o sino quando chega uma notificação nova
  useEffect(() => {
    if (!lastEvent) return;
    setIsRinging(true);
    const timeout = setTimeout(() => setIsRinging(false), 2500);
    return () => clearTimeout(timeout);
  }, [lastEvent]);

  useEffect(() => {
    if (isOpen) loadInbox();
  }, [isOpen, loadInbox]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen]);

  const handleItemClick = (item: InboxItem) => {
    if (!item.read_at) markNotificationsRead([item.id]);
    setIsOpen(false);
    navigate(item.data?.link || '/admin/leads');
  };

  const BellIcon = isRinging ? BellRing : Bell;

  return (
    <div ref={containerRef}>
      <button
        onClick={() => setIsOpen(open => !open)}
        className={`fixed z-30 top-4 right-4 lg:top-auto lg:bottom-6 lg:right-6 h-10 w-10 lg:h-12 lg:w-12 rounded-full bg-white border border-tinta/15 shadow-sm lg:shadow-md flex items-center justify-center text-tinta-soft hover:text-bordo hover:border-bordo/40 transition-colors ${
          isOpen ? 'text-bordo border-bordo/40' : ''
        }`}
        aria-label={unreadNotifications > 0 ? `Notificações (${unreadNotifications} não lidas)` : 'Notificações'}
        aria-expanded={isOpen}
      >
        <BellIcon className={`h-5 w-5 lg:h-6 lg:w-6 ${isRinging ? 'animate-bounce text-bordo' : ''}`} />
        {unreadNotifications > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-bordo px-1 text-[11px] font-bold text-creme ring-2 ring-white">
            {unreadNotifications > 99 ? '99+' : unreadNotifications}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="fixed z-40 inset-x-2 top-16 lg:inset-x-auto lg:top-auto lg:right-6 lg:bottom-20 lg:w-[24rem] max-h-[75vh] lg:max-h-[70vh] flex flex-col bg-white rounded-xl shadow-lg border border-tinta/10 font-body overflow-hidden">
          {/* Cabeçalho */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-tinta/10">
            <h3 className="font-display text-lg font-bold text-tinta">Notificações</h3>
            <div className="flex items-center gap-1">
              {unreadNotifications > 0 && (
                <button
                  onClick={markAllNotificationsRead}
                  className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold text-bordo hover:bg-bordo/5"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Marcar todas como lidas
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-md text-tinta-soft/70 hover:text-tinta hover:bg-creme-light"
                aria-label="Fechar notificações"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Ativar push neste dispositivo */}
          {pushStatus === 'default' && (
            <div className="mx-3 mt-3 p-3 rounded-lg border border-mostarda/40 bg-mostarda/10 flex items-center gap-3">
              <BellRing className="h-5 w-5 text-mostarda-dark flex-shrink-0" />
              <p className="flex-1 text-xs text-tinta-soft">
                Receba um aviso no celular ou computador a cada novo cliente.
              </p>
              <button
                onClick={enablePush}
                className="px-3 py-1.5 bg-bordo hover:bg-bordo-dark text-creme text-xs font-bold rounded-lg transition-colors whitespace-nowrap"
              >
                Ativar
              </button>
            </div>
          )}
          {pushStatus === 'needs-install' && (
            <div className="mx-3 mt-3 p-3 rounded-lg border border-tinta/10 bg-creme-light">
              <p className="text-xs text-tinta-soft mb-2">
                No iPhone, instale o aplicativo na tela de início para receber avisos de novos clientes.
              </p>
              <InstallAppButton />
            </div>
          )}

          {/* Lista */}
          <div className="flex-1 overflow-y-auto py-1">
            {inbox.length === 0 && inboxLoading && (
              <div className="flex justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-tinta-soft/60" />
              </div>
            )}

            {inbox.length === 0 && !inboxLoading && (
              <div className="flex flex-col items-center text-center px-6 py-10">
                <div className="h-12 w-12 rounded-full bg-creme-light border border-tinta/10 flex items-center justify-center mb-3">
                  <Bell className="h-5 w-5 text-tinta-soft/60" />
                </div>
                <p className="text-sm font-semibold text-tinta">Nenhuma notificação por aqui</p>
                <p className="text-xs text-tinta-soft mt-1">Avisaremos assim que um novo cliente participar.</p>
              </div>
            )}

            {inbox.map(item => {
              const won = !!item.data?.won;
              const Icon = won ? Trophy : UserPlus;
              return (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-creme-light ${
                    item.read_at ? '' : 'bg-bordo/[0.03]'
                  }`}
                >
                  <div className={`h-9 w-9 flex-shrink-0 rounded-full flex items-center justify-center ${won ? 'bg-mostarda/20' : 'bg-bordo/10'}`}>
                    <Icon className={`h-4 w-4 ${won ? 'text-mostarda-dark' : 'text-bordo'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${item.read_at ? 'text-tinta-soft font-medium' : 'text-tinta font-bold'}`}>
                      {item.title}
                    </p>
                    {item.body && <p className="text-sm text-tinta-soft truncate">{item.body}</p>}
                    <p className="text-[11px] text-tinta-soft/70 mt-0.5">{moment(item.created_at).fromNow()}</p>
                  </div>
                  {!item.read_at && <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-bordo" aria-label="Não lida" />}
                </button>
              );
            })}

            {hasMoreInbox && (
              <button
                onClick={() => loadInbox(true)}
                disabled={inboxLoading}
                className="w-full py-2.5 text-xs font-semibold text-bordo hover:bg-creme-light disabled:opacity-50"
              >
                {inboxLoading ? 'Carregando...' : 'Carregar mais'}
              </button>
            )}
          </div>

          {/* Rodapé */}
          <Link
            to="/admin/notifications"
            onClick={() => setIsOpen(false)}
            className="flex items-center justify-center gap-1.5 px-4 py-2.5 border-t border-tinta/10 text-xs font-semibold text-tinta-soft hover:text-bordo hover:bg-creme-light"
          >
            <Settings className="h-3.5 w-3.5" />
            Configurar notificações
          </Link>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
